import type { ReactNode } from 'react';

/**
 * Gráficos do painel de relatórios, em HTML e CSS puros — são barras e proporções, e uma
 * biblioteca de gráficos custaria mais bytes do que o resto da tela inteira.
 *
 * Três coisas valem para todos (decisões de 28/09):
 *
 * 1. **O valor fica escrito na barra.** Antes só aparecia ao passar o cursor, o que não
 *    existe no celular e obriga a caçar número por número em qualquer lugar.
 * 2. **Clicar filtra o painel inteiro**, como em BI: um clique troca a seleção, Ctrl (ou
 *    Cmd) acumula, clicar de novo no que já estava tira.
 * 3. **A barra tem dois corpos**: o vulto claro é o total do período, o corpo sólido é o
 *    que sobrou da seleção. Assim o gráfico clicado continua inteiro na tela — com o
 *    resto apagado, mas ali, para se poder voltar atrás.
 */

/** Uma coluna do gráfico vertical. */
export interface Coluna {
  /** O que identifica esta fatia na seleção (o dia, a hora, o id do setor…). */
  chave: string;
  rotulo: string;
  /** Valor com a seleção aplicada. */
  valor: number;
  /** Valor sem a seleção — o vulto de trás. Igual ao `valor` quando nada está selecionado. */
  total: number;
  /** Texto do title (o detalhe que não cabe escrito). */
  descricao?: string;
}

interface PropsSelecao {
  /** Chaves selecionadas agora. Vazio = nada selecionado, tudo aceso. */
  selecao?: string[];
  /** `acumular` vem do Ctrl/Cmd. Sem handler, o gráfico é só de leitura. */
  aoSelecionar?: (chave: string, acumular: boolean) => void;
}

const ALTURA_BARRA = 132;

/**
 * Colunas verticais: volume por dia e por hora.
 *
 * Cada coluna é uma grade de três linhas — barra, valor, rótulo — e as três linhas têm
 * altura fixa. É o que conserta as barras desalinhadas do "Por hora do dia": antes o
 * rótulo só existia de três em três horas, e a coluna sem rótulo ficava mais curta,
 * empurrando a barra para baixo de um jeito que parecia dado errado.
 */
export function Colunas({
  dados,
  cor = 'marca',
  selecao = [],
  aoSelecionar,
  vazio = 'Nada no período.',
}: PropsSelecao & {
  dados: Coluna[];
  cor?: 'marca' | 'teal';
  vazio?: string;
}) {
  if (dados.length === 0) return <Vazio texto={vazio} />;
  const maior = Math.max(...dados.map((d) => d.total), 1);
  const temSelecao = selecao.length > 0;
  // Com muitos dias não cabe um rótulo por coluna; mostra de tantos em tantos.
  const passo = Math.max(1, Math.ceil(dados.length / 16));

  return (
    <div className="flex items-end gap-px overflow-x-auto pb-1" style={{ minHeight: 178 }}>
      {dados.map((d, i) => {
        const escolhida = selecao.includes(d.chave);
        const apagada = temSelecao && !escolhida;
        return (
          <button
            key={d.chave}
            type="button"
            disabled={!aoSelecionar}
            onClick={(e) => aoSelecionar?.(d.chave, e.ctrlKey || e.metaKey || e.shiftKey)}
            title={d.descricao ?? `${d.rotulo}: ${d.valor}`}
            aria-pressed={escolhida}
            className={`group flex min-w-5 flex-1 flex-col items-center gap-1 rounded-t px-px transition ${
              aoSelecionar ? 'cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800' : ''
            } ${apagada ? 'opacity-40' : ''}`}
          >
            <span className="flex w-full items-end justify-center" style={{ height: ALTURA_BARRA }}>
              {/* O vulto de trás é o total do período; o corpo da frente, a fatia viva. */}
              <span
                className="relative flex w-full items-end justify-center rounded-t bg-neutral-200/70 dark:bg-neutral-700/60"
                style={{ height: `${Math.max(2, (d.total / maior) * ALTURA_BARRA)}px` }}
              >
                <span
                  className={`absolute inset-x-0 bottom-0 rounded-t ${
                    cor === 'teal' ? 'bg-teal-500' : 'bg-marca-500'
                  } ${escolhida ? 'ring-2 ring-neutral-900 dark:ring-white' : ''}`}
                  style={{
                    height: `${Math.max(d.valor > 0 ? 2 : 0, (d.valor / maior) * ALTURA_BARRA)}px`,
                  }}
                />
              </span>
            </span>
            {/* O número por escrito, embaixo da barra: sem ele só o cursor conta a história. */}
            <span className="h-4 text-[10px] font-medium tabular-nums">
              {d.valor > 0 ? d.valor : ''}
            </span>
            <span className="h-4 w-full truncate text-[10px] text-neutral-500">
              {i % passo === 0 || escolhida ? d.rotulo : ''}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Uma fatia da lista com barra proporcional — motivos, setores, quem quer que seja. */
export interface Fatia {
  chave: string;
  rotulo: string;
  valor: number;
  total: number;
  detalhe?: string;
}

/** Lista com barra proporcional: cabe nome comprido, que é o caso de motivo e de setor. */
export function Barras({
  itens,
  selecao = [],
  aoSelecionar,
  vazio = 'Nada no período.',
}: PropsSelecao & { itens: Fatia[]; vazio?: string }) {
  if (itens.length === 0) return <Vazio texto={vazio} />;
  const maior = Math.max(...itens.map((i) => i.total), 1);
  const temSelecao = selecao.length > 0;

  return (
    <ul className="space-y-1.5">
      {itens.map((item) => {
        const escolhida = selecao.includes(item.chave);
        return (
          <li key={item.chave}>
            <button
              type="button"
              disabled={!aoSelecionar}
              onClick={(e) => aoSelecionar?.(item.chave, e.ctrlKey || e.metaKey || e.shiftKey)}
              aria-pressed={escolhida}
              className={`block w-full rounded px-1 py-0.5 text-left transition ${
                aoSelecionar ? 'cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800' : ''
              } ${temSelecao && !escolhida ? 'opacity-40' : ''} ${
                escolhida ? 'bg-neutral-100 dark:bg-neutral-800' : ''
              }`}
            >
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="truncate">{item.rotulo}</span>
                <span className="shrink-0 tabular-nums text-neutral-500">
                  {item.detalhe ?? item.valor}
                </span>
              </div>
              <div className="mt-1 h-2 rounded-full bg-neutral-200/70 dark:bg-neutral-700/60">
                <div
                  className="h-2 rounded-full bg-neutral-300 dark:bg-neutral-600"
                  style={{ width: `${(item.total / maior) * 100}%` }}
                >
                  <div
                    className="h-2 rounded-full bg-marca-500"
                    style={{ width: item.total ? `${(item.valor / item.total) * 100}%` : '0%' }}
                  />
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Tabela que também é filtro: clicar na linha do atendente recorta o painel por ele.
 *
 * Mesmas regras do gráfico — clique troca, Ctrl acumula, clique de novo desfaz —, porque
 * pedir para achar o mesmo nome num select depois de vê-lo na tabela é trabalho à toa.
 */
export function TabelaFiltravel<T>({
  itens,
  chave,
  colunas,
  selecao = [],
  aoSelecionar,
  vazio,
}: PropsSelecao & {
  itens: T[];
  chave: (item: T) => string;
  colunas: { titulo: string; alinhar?: 'direita'; conteudo: (item: T) => ReactNode }[];
  vazio: string;
}) {
  const temSelecao = selecao.length > 0;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-left text-neutral-500">
          <tr>
            {colunas.map((c) => (
              <th key={c.titulo} className={`pb-1 ${c.alinhar === 'direita' ? 'text-right' : ''}`}>
                {c.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {itens.length === 0 && (
            <tr>
              <td colSpan={colunas.length} className="py-4 text-center text-neutral-500">
                {vazio}
              </td>
            </tr>
          )}
          {itens.map((item) => {
            const k = chave(item);
            const escolhida = selecao.includes(k);
            return (
              <tr
                key={k}
                onClick={(e) => aoSelecionar?.(k, e.ctrlKey || e.metaKey || e.shiftKey)}
                aria-selected={escolhida}
                className={`border-t border-neutral-100 transition dark:border-neutral-800 ${
                  aoSelecionar
                    ? 'cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800'
                    : ''
                } ${temSelecao && !escolhida ? 'opacity-40' : ''} ${
                  escolhida ? 'bg-neutral-100 dark:bg-neutral-800' : ''
                }`}
              >
                {colunas.map((c) => (
                  <td
                    key={c.titulo}
                    className={`py-1 ${c.alinhar === 'direita' ? 'text-right tabular-nums' : ''}`}
                  >
                    {c.conteudo(item)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Vazio({ texto }: { texto: string }) {
  return <p className="py-6 text-center text-xs text-neutral-500">{texto}</p>;
}

/** "2026-09-28" → "28/09". */
export function formatarDia(dia: string): string {
  const [, mes, d] = dia.split('-');
  return `${d}/${mes}`;
}
